import React, { useMemo } from 'react';
import EDBox from '../../../../../../../../BaseComponents/EDBox/EDBox';
import EDTypography from '../../../../../../../../BaseComponents/Typography/EDTypography';
import { IOverlappedBooking } from '../../../AktivitetResursValjare';
import { useIsSmallScreen } from '../../../../../../../../Hooks/useFullscreenView';
import { themeColors } from '../../../../../../../../BaseComponents/ThemeProvider/AvenyThemeColors';

interface IProps {
  allDubbelBookings: IOverlappedBooking[];
}

const CopyActivityDoubleBooking: React.FC<
  React.PropsWithChildren<IProps>
> = props => {
  const isSmallScreen = useIsSmallScreen();
  const allDubbelBookings = props.allDubbelBookings;

  // Group bookings by resource name
  const groupedBookings = useMemo(() => {
    const groups: { [key: string]: IOverlappedBooking[] } = {};

    allDubbelBookings.forEach((booking: IOverlappedBooking) => {
      if (!groups[booking.resourceName]) {
        groups[booking.resourceName] = [];
      }
      groups[booking.resourceName].push(booking);
    });

    // Sort bookings within each group by start time
    Object.keys(groups).forEach((resourceName: string) => {
      groups[resourceName].sort((a, b) => a.resStart.localeCompare(b.resStart));
    });

    return groups;
  }, [allDubbelBookings]);

  const resourceNames = Object.keys(groupedBookings);

  return (
    <EDBox
      display="flex"
      marginTop={'5px'}
      flexDirection={'column'}
      padding={'0px'}
      paddingRight={'10px'}
      paddingBottom={'10px'}
      data-testid="double-bookings"
    >
      <EDTypography
        variant="overline"
        color="textSecondary"
        bold
        width={'100%'}
        marginRight={'8px'}
        marginBlock={'10px 5px'}
        marginBottom={'10px'}
        paddingLeft={'15px'}
      >
        DUBBELBOKNINGAR:
      </EDTypography>

      {resourceNames.map((resourceName: string, groupIndex: number) => (
        <EDBox
          key={resourceName}
          marginBottom={
            groupIndex !== resourceNames.length - 1 ? '12px' : '5px'
          }
          sx={{
            borderLeft: `4px solid ${themeColors.orange[700]}`,
            paddingLeft: '12px',
          }}
        >
          <EDTypography
            fontSize={'13px'}
            fontWeight="bold"
            color="black"
            marginBottom={'5px'}
          >
            {resourceName} med:
          </EDTypography>

          {groupedBookings[resourceName].map(
            (item: IOverlappedBooking, index: number) => {
              const isLastItemInGroup =
                index === groupedBookings[resourceName].length - 1;

              return (
                <EDBox
                  key={index}
                  display="flex"
                  alignItems="center"
                  marginBottom={'3px'}
                  marginLeft={'16px'}
                  sx={{
                    borderBottom:
                      isSmallScreen && !isLastItemInGroup
                        ? `1px solid ${themeColors.grayFade[200.1]}`
                        : 'none',
                    paddingBottom:
                      isSmallScreen && !isLastItemInGroup ? '8px' : '0px',
                  }}
                >
                  <EDTypography
                    fontSize={'13px'}
                    color="black"
                    marginRight="8px"
                    flex={isSmallScreen ? '5' : undefined}
                  >
                    {item.aktTitel}
                  </EDTypography>
                  {!isSmallScreen && (
                    <EDTypography fontSize={'20px'} color="black">
                      {' '}
                      ·{' '}
                    </EDTypography>
                  )}
                  <EDTypography
                    flex={isSmallScreen ? '3' : undefined}
                    fontSize={'13px'}
                    marginLeft={'7px'}
                    color="black"
                    marginRight="8px"
                    sx={{
                      display: isSmallScreen ? 'flex' : 'inline',
                      flexDirection: isSmallScreen ? 'column' : 'row',
                    }}
                  >
                    {item.bokNr === '' ? (
                      'outlook'
                    ) : (
                      <>
                        {isSmallScreen ? (
                          <>
                            <span>boknr:</span>
                            <span>{item.bokNr}</span>
                          </>
                        ) : (
                          `boknr: ${item.bokNr}`
                        )}
                      </>
                    )}
                  </EDTypography>
                  {!isSmallScreen && (
                    <EDTypography fontSize={'20px'} color="black">
                      {' '}
                      ·{' '}
                    </EDTypography>
                  )}
                  <EDTypography
                    flex={isSmallScreen ? '4' : undefined}
                    fontSize={'13px'}
                    marginLeft={'7px'}
                    color="black"
                    marginRight="8px"
                    sx={{
                      display: isSmallScreen ? 'flex' : 'inline',
                      flexDirection: isSmallScreen ? 'column' : 'row',
                      whiteSpace: isSmallScreen ? 'normal' : 'nowrap',
                    }}
                  >
                    <span style={{ whiteSpace: 'nowrap' }}>
                      {item.resStart.substring(0, 10)}
                    </span>
                    {!isSmallScreen && ' '}
                    <span style={{ whiteSpace: 'nowrap' }}>
                      {item.resStart.substring(11) +
                        ' - ' +
                        item.resEnd.substring(11)}
                    </span>
                  </EDTypography>
                </EDBox>
              );
            }
          )}
        </EDBox>
      ))}
    </EDBox>
  );
};

export default CopyActivityDoubleBooking;
